import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { usePlByProgramStyles, PlPageHeader } from "./plByProgramTheme";
import { useReportFonts } from "../../reportFonts";
import { getActualsVsForecastByDateRangeAPI } from "../../../../api/reports-api";
import Loading from "../../../../common-components/Loading";
import ActualsVsForecastByDateRangePicker from "./ActualsVsForecastByDateRangePicker";
import ActualsVsForecastByDateRangeTable from "./ActualsVsForecastByDateRangeTable";
import {
  buildApiFilters,
  colsFromKeys,
  normalizePct,
  computeVariance,
  defaultDateRange,
  formatActualsThroughFromMonthKeys,
  formatRangeLabel,
} from "./actualsVsForecastByDateRangeUtils";

export default function ActualsVsForecastByDateRange({ filters, onParamsChange }) {
  const plClasses = usePlByProgramStyles();
  useReportFonts();
  const classes = plClasses;

  const [range, setRange] = useState(defaultDateRange);
  const [cols, setCols] = useState([]);
  const [live, setLive] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [liveVersion, setLiveVersion] = useState(null);
  const [comparisonVersion, setComparisonVersion] = useState(null);
  const [actualCols, setActualCols] = useState(new Set());
  const [comparisonActualCols, setComparisonActualCols] = useState(new Set());
  const [actualsThrough, setActualsThrough] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiFilterKey = useMemo(() => JSON.stringify(buildApiFilters(filters)), [filters]);
  const { startMonth, startYear, endMonth, endYear } = range;

  /* Lets the parent's Download button send the currently-selected range —
     the range picker lives here, not in the parent. */
  useEffect(() => {
    onParamsChange?.({ startMonth, startYear, endMonth, endYear });
  }, [startMonth, startYear, endMonth, endYear, onParamsChange]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getActualsVsForecastByDateRangeAPI(buildApiFilters(filters), { startMonth, startYear, endMonth, endYear })
      .then((res) => {
        if (res?.status === 200 && res.data) {
          const {
            columns,
            actualMonths,
            comparisonActualMonths,
            live: liveRaw,
            comparison: comparisonRaw,
            liveVersion: lv,
            comparisonVersion: cv,
          } = res.data;
          const colDefs = colsFromKeys(columns);
          const colKeys = colDefs.map((c) => c.key);
          setCols(colDefs);
          setLive(normalizePct(liveRaw, colKeys));
          setComparison(normalizePct(comparisonRaw, colKeys));
          setActualCols(new Set(actualMonths || []));
          setComparisonActualCols(new Set(comparisonActualMonths || []));
          setActualsThrough(formatActualsThroughFromMonthKeys(actualMonths));
          setLiveVersion(lv ?? null);
          setComparisonVersion(cv ?? null);
        } else {
          setError(res?.message || "Failed to load data");
        }
      })
      .catch(() => setError("Failed to load data"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiFilterKey, startMonth, startYear, endMonth, endYear]);

  const colKeys = useMemo(() => cols.map((c) => c.key), [cols]);
  const variance = live && comparison ? computeVariance(live, comparison, colKeys) : null;

  const wrapRefs = useRef([null, null, null]);
  const isSyncing = useRef(false);
  const handleScroll = useCallback((srcIdx, e) => {
    if (isSyncing.current) return;
    isSyncing.current = true;
    const { scrollLeft } = e.target;
    wrapRefs.current.forEach((el, i) => { if (i !== srcIdx && el) el.scrollLeft = scrollLeft; });
    requestAnimationFrame(() => { isSyncing.current = false; });
  }, []);

  const liveLabel = liveVersion?.name ?? "Live Forecast";
  const comparisonLabel = comparisonVersion?.name ?? "Comparison";
  const rangeLabel = formatRangeLabel(startMonth, startYear, endMonth, endYear);

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <PlPageHeader
          classes={classes}
          title="Actuals Vs Forecast by Date Range"
          accent="Live Forecast"
          meta="Monthly P&L Actuals and Forecast Across a Custom Date Range"
          metaTop={rangeLabel}
          date={actualsThrough}
        />

        <ActualsVsForecastByDateRangePicker
          startMonth={startMonth}
          startYear={startYear}
          endMonth={endMonth}
          endYear={endYear}
          onChange={setRange}
        />

        {loading && <Loading />}

        {!loading && error && (
          <div style={{ padding: 40, textAlign: "center", color: "#DC2626", fontSize: 14 }}>{error}</div>
        )}

        {!loading && !error && (
          <>
            <ActualsVsForecastByDateRangeTable
              data={live} isVariance={false} cols={cols} actualCols={actualCols}
              title={`Actuals + ${liveLabel}`}
              meta={`${rangeLabel} · Monthly Breakdown`}
              wrapRef={(el) => { wrapRefs.current[0] = el; }} onScroll={(e) => handleScroll(0, e)}
            />
            <ActualsVsForecastByDateRangeTable
              data={comparison} isVariance={false} cols={cols} actualCols={comparisonActualCols}
              title={`Actuals + ${comparisonLabel} Forecast`}
              meta={`${rangeLabel} · ${comparisonLabel} Forecast Scenario`}
              wrapRef={(el) => { wrapRefs.current[1] = el; }} onScroll={(e) => handleScroll(1, e)}
            />
            <ActualsVsForecastByDateRangeTable
              data={variance} isVariance={true} cols={cols} actualCols={actualCols}
              title="Variance"
              meta={`${liveLabel} Vs. ${comparisonLabel} Forecast`}
              wrapRef={(el) => { wrapRefs.current[2] = el; }} onScroll={(e) => handleScroll(2, e)}
            />
          </>
        )}
      </div>
    </div>
  );
}
